import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import { addToCart } from "../redux/cartSlice";
import { fetchProductsFromFirestore } from "../firebase/firebaseUtils";

/* ─── Design tokens ───────────────────────────────────────── */
const T = {
  rose:      "#c0506e",
  roseDark:  "#8f2d46",
  roseLight: "#e8839a",
  roseSoft:  "#fdf0f3",
  roseMid:   "#f3c5cf",
  ivory:     "#faf8f5",
  ivoryDark: "#f0ece6",
  warm100:   "#e8e3dc",
  warm200:   "#d4cfc8",
  warm400:   "#9e9189",
  warm700:   "#3d3530",
  ink:       "#1a1510",
  ffDisplay: "'Cormorant Garamond', Georgia, serif",
  ffBody:    "'Outfit', system-ui, sans-serif",
  easeLux:   "cubic-bezier(0.16, 1, 0.3, 1)",
  easeSnap:  "cubic-bezier(0.34, 1.56, 0.64, 1)",
};

const ITEMS_PER_PAGE = 9;

const FILTERS = ["In Stock", "On Sale", "New Arrival", "Best Seller"];
const SORTS = [
  { value: "default",    label: "Featured" },
  { value: "price-asc",  label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "rating",     label: "Top Rated" },
];

/* ─── Accent bar ──────────────────────────────────────────── */
const AccentBar = () => (
  <div style={{
    height: 3,
    background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)`,
  }} />
);

/* ─── Spinner ─────────────────────────────────────────────── */
const Spinner = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", padding: "5rem 0" }}>
    <div style={{
      width: 40, height: 40,
      border: `3px solid ${T.roseMid}`,
      borderTopColor: T.rose,
      borderRadius: "50%",
      animation: "spin .8s linear infinite",
    }} />
    <p style={{ fontFamily: T.ffBody, fontSize: 13, color: T.warm400, margin: 0 }}>Loading products…</p>
  </div>
);

/* ─── Wishlist button ─────────────────────────────────────── */
const WishBtn = ({ wished, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title={wished ? "Remove from wishlist" : "Add to wishlist"}
      aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
      style={{
        position: "absolute", top: 10, right: 10,
        width: 34, height: 34,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: wished ? T.roseSoft : "#ffffffdd",
        border: `1.5px solid ${wished || hov ? T.rose : "transparent"}`,
        borderRadius: "50%",
        cursor: "pointer",
        fontSize: 15,
        backdropFilter: "blur(4px)",
        transition: "all 200ms",
        boxShadow: "0 2px 8px rgba(0,0,0,.1)",
      }}
    >{wished ? "♥" : hov ? "♥" : "♡"}</button>
  );
};

/* ─── Product card ────────────────────────────────────────── */
const ProductCard = ({ product, onAddToCart, wished, onWish, idx }) => {
  const [hov, setHov] = useState(false);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    onAddToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${hov ? T.roseMid : T.warm100}`,
        overflow: "hidden",
        boxShadow: hov
          ? `0 12px 40px rgba(192,80,110,.14), 0 2px 8px rgba(26,21,16,.06)`
          : `0 2px 8px rgba(26,21,16,.04)`,
        transform: hov ? "translateY(-4px)" : "translateY(0)",
        transition: `transform 260ms ${T.easeSnap}, box-shadow 240ms ${T.easeLux}, border-color 200ms`,
        animation: `fadeUp 380ms ${idx * 55}ms ${T.easeLux} both`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Image */}
      <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", background: T.ivoryDark }}>
        <img
          src={product.imageUrl || "/placeholder.jpg"}
          alt={product.name}
          loading="lazy"
          style={{
            width: "100%", height: "100%",
            objectFit: "cover",
            transform: hov ? "scale(1.06)" : "scale(1)",
            transition: `transform 500ms ${T.easeLux}`,
            display: "block",
          }}
        />
        {product.discount && (
          <span style={{
            position: "absolute", top: 10, left: 10,
            background: T.rose, color: "#fff",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
            padding: "3px 9px", borderRadius: 99, fontFamily: T.ffBody,
            boxShadow: `0 2px 8px rgba(192,80,110,.3)`,
          }}>
            {product.discount}% off
          </span>
        )}
        {product.soldOut && (
          <span style={{
            position: "absolute", top: 10, left: 10,
            background: "#e07070", color: "#fff",
            fontSize: 11, fontWeight: 700, padding: "3px 9px",
            borderRadius: 99, fontFamily: T.ffBody,
          }}>Sold Out</span>
        )}
        <WishBtn wished={wished} onClick={() => onWish(product.id)} />
      </div>

      {/* Content */}
      <div style={{ padding: "clamp(0.9rem, 2vw, 1.1rem)", display: "flex", flexDirection: "column", gap: "0.35rem", flex: 1 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.warm400, fontFamily: T.ffBody }}>
          {product.category || "Hair Collection"}
        </p>

        <p style={{ margin: 0, fontFamily: T.ffDisplay, fontWeight: 700, fontSize: "clamp(1rem, 2.5vw, 1.15rem)", color: T.ink, lineHeight: 1.2 }}>
          {product.name}
        </p>

        {/* Stars — only if real rating exists */}
        {product.rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ color: "#f59e0b", fontSize: 12 }}>
              {"★".repeat(Math.round(product.rating))}{"☆".repeat(5 - Math.round(product.rating))}
            </span>
            <span style={{ fontSize: 11, color: T.warm400, fontFamily: T.ffBody }}>
              {product.rating} {product.reviewCount ? `(${product.reviewCount})` : ""}
            </span>
          </div>
        )}

        {/* Price */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: "0.35rem" }}>
          <span style={{ fontFamily: T.ffDisplay, fontWeight: 700, fontSize: "clamp(1.1rem, 3vw, 1.25rem)", color: T.roseDark }}>
            GH₵{Number(product.price).toFixed(2)}
          </span>
          {product.originalPrice && (
            <span style={{ fontFamily: T.ffBody, fontSize: 12, color: T.warm400, textDecoration: "line-through" }}>
              GH₵{Number(product.originalPrice).toFixed(2)}
            </span>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={handleAdd}
          disabled={product.soldOut}
          className="add-btn"
          style={{
            marginTop: "0.65rem",
            width: "100%",
            padding: "0.7rem",
            fontFamily: T.ffBody,
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "0.04em",
            color: added ? T.rose : product.soldOut ? T.warm400 : "#fff",
            background: added ? T.roseSoft : product.soldOut ? T.warm100 : T.rose,
            border: `1.5px solid ${added ? T.rose : "transparent"}`,
            borderRadius: 10,
            cursor: product.soldOut ? "not-allowed" : "pointer",
            transition: `background 250ms, color 250ms, border-color 250ms, transform 180ms ${T.easeSnap}`,
          }}
        >
          {product.soldOut ? "Out of Stock" : added ? "✓ Added to Cart" : "Add to Cart"}
        </button>
      </div>
    </div>
  );
};

/* ─── Sidebar section ─────────────────────────────────────── */
const SideSection = ({ title, children }) => (
  <div style={{ marginBottom: "1.25rem" }}>
    <p style={{ margin: "0 0 .65rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: T.rose, fontFamily: T.ffBody }}>
      {title}
    </p>
    {children}
  </div>
);

/* ─── Pagination ──────────────────────────────────────────── */
const PagerBtn = ({ children, active, disabled, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 36, height: 36,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: T.ffBody, fontSize: 13, fontWeight: 600,
        borderRadius: 9,
        border: `1.5px solid ${active ? T.rose : hov && !disabled ? T.rose : T.warm200}`,
        background: active ? T.rose : hov && !disabled ? T.roseSoft : "#fff",
        color: active ? "#fff" : disabled ? T.warm200 : hov ? T.rose : T.warm700,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 160ms",
      }}
    >{children}</button>
  );
};

const Pager = ({ page, total, onChange }) => {
  const pages = Math.ceil(total / ITEMS_PER_PAGE);
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: "3rem", flexWrap: "wrap" }}>
      <PagerBtn disabled={page === 1} onClick={() => onChange(page - 1)}>←</PagerBtn>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <PagerBtn key={p} active={p === page} onClick={() => onChange(p)}>{p}</PagerBtn>
      ))}
      <PagerBtn disabled={page === pages} onClick={() => onChange(page + 1)}>→</PagerBtn>
    </div>
  );
};

/* ─── Mobile filter drawer ────────────────────────────────── */
const FilterDrawer = ({ open, onClose, children }) => (
  <>
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 400,
        background: "rgba(26,21,16,.4)",
        backdropFilter: "blur(2px)",
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
        transition: "opacity 300ms",
      }}
    />
    <div style={{
      position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 410,
      width: "min(300px, 85vw)",
      background: "#fff",
      transform: open ? "translateX(0)" : "translateX(-100%)",
      transition: `transform 340ms ${T.easeLux}`,
      overflowY: "auto",
      boxShadow: open ? "8px 0 48px rgba(26,21,16,.15)" : "none",
    }}>
      <AccentBar />
      <div style={{ padding: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <p style={{ margin: 0, fontFamily: T.ffDisplay, fontSize: "1.2rem", fontWeight: 700, color: T.ink }}>Filters</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: T.warm400 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  </>
);

/* ─── Sidebar content (shared between desktop + mobile) ───── */
const SidebarContent = ({ priceRange, setPriceRange, activeFilters, toggleFilter, sortBy, setSortBy, setPage, clearFilters }) => (
  <>
    <SideSection title="Price Range">
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: T.warm400, fontFamily: T.ffBody }}>GH₵{priceRange[0]}</span>
        <span style={{ fontSize: 12, color: T.warm400, fontFamily: T.ffBody }}>GH₵{priceRange[1]}</span>
      </div>
      <input
        type="range" min={0} max={500}
        value={priceRange[1]}
        onChange={(e) => { setPriceRange([priceRange[0], Number(e.target.value)]); setPage(1); }}
        style={{ width: "100%", accentColor: T.rose }}
      />
    </SideSection>

    <div style={{ height: 1, background: T.warm100, margin: "1rem 0" }} />

    <SideSection title="Filter">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {FILTERS.map((f) => (
          <label key={f} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={activeFilters.includes(f)}
              onChange={() => { toggleFilter(f); setPage(1); }}
              style={{ accentColor: T.rose, width: 14, height: 14 }}
            />
            <span style={{ fontFamily: T.ffBody, fontSize: 13, color: T.warm700 }}>{f}</span>
          </label>
        ))}
      </div>
    </SideSection>

    <div style={{ height: 1, background: T.warm100, margin: "1rem 0" }} />

    <SideSection title="Sort By">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {SORTS.map((s) => (
          <label key={s.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="radio"
              name="sort"
              checked={sortBy === s.value}
              onChange={() => { setSortBy(s.value); setPage(1); }}
              style={{ accentColor: T.rose, width: 14, height: 14 }}
            />
            <span style={{ fontFamily: T.ffBody, fontSize: 13, color: T.warm700 }}>{s.label}</span>
          </label>
        ))}
      </div>
    </SideSection>

    {(activeFilters.length > 0 || sortBy !== "default" || priceRange[1] < 500) && (
      <button
        onClick={clearFilters}
        style={{
          width: "100%", padding: "8px",
          fontFamily: T.ffBody, fontSize: 12, fontWeight: 600,
          color: T.rose, background: T.roseSoft,
          border: `1.5px solid ${T.roseMid}`,
          borderRadius: 8, cursor: "pointer", marginTop: "0.5rem",
        }}
      >Clear All Filters</button>
    )}
  </>
);

/* ═══════════════ Main component ════════════════════════════ */
const ShopPage = () => {
  const dispatch = useDispatch();

  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [wished, setWished]             = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("glh_wishlist") || "[]")); }
    catch { return new Set(); }
  });
  const [page, setPage]                 = useState(1);
  const [priceRange, setPriceRange]     = useState([0, 500]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy]             = useState("default");
  const [drawerOpen, setDrawerOpen]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchProductsFromFirestore();
        setProducts(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Persist wishlist to localStorage */
  useEffect(() => {
    localStorage.setItem("glh_wishlist", JSON.stringify([...wished]));
  }, [wished]);

  const toggleFilter = useCallback((f) =>
    setActiveFilters((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    ), []);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
    setPriceRange([0, 500]);
    setSortBy("default");
    setPage(1);
  }, []);

  const toggleWish = useCallback((id) =>
    setWished((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    }), []);

  const handleAddToCart = useCallback((product) => {
    dispatch(addToCart(product));
  }, [dispatch]);

  /* ✅ Fixed: activeFilters now applied in pipeline + in deps array */
  const filtered = useMemo(() => {
    let out = products.filter(
      (p) => Number(p.price) >= priceRange[0] && Number(p.price) <= priceRange[1]
    );
    if (activeFilters.includes("In Stock"))    out = out.filter((p) => !p.soldOut && p.quantity > 0);
    if (activeFilters.includes("On Sale"))     out = out.filter((p) => !!p.discount);
    if (activeFilters.includes("New Arrival")) out = out.filter((p) => !!p.isNew);
    if (activeFilters.includes("Best Seller")) out = out.filter((p) => !!p.isBestSeller);
    if (sortBy === "price-asc")  out = [...out].sort((a, b) => a.price - b.price);
    if (sortBy === "price-desc") out = [...out].sort((a, b) => b.price - a.price);
    if (sortBy === "rating")     out = [...out].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return out;
  }, [products, priceRange, sortBy, activeFilters]);

  const paginated = useMemo(() =>
    filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
  [filtered, page]);

  const sidebarProps = { priceRange, setPriceRange, activeFilters, toggleFilter, sortBy, setSortBy, setPage, clearFilters };

  return (
    <>
      <Helmet>
        <title>Shop | Glossy Lux Hair — Premium Wigs & Extensions</title>
        <meta name="description" content="Browse our full collection of premium human hair wigs, lace fronts and extensions. Filter by price, sort by rating and find your perfect style at Glossy Lux Hair." />
        <link rel="canonical" href="https://glossyluxhair.com/shop" />
        <meta property="og:title" content="Shop | Glossy Lux Hair" />
        <meta property="og:description" content="Premium human hair wigs and extensions. Filter, sort and shop our full collection." />
        <meta property="og:url" content="https://glossyluxhair.com/shop" />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin   { to { transform:rotate(360deg) } }
        .add-btn:not(:disabled):hover { transform: translateY(-1px) !important; }
        .add-btn { transition: background 250ms, color 250ms, border-color 250ms, transform 180ms; }

        /* ── Responsive layout ── */
        .shop-layout {
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: clamp(1.5rem, 3vw, 2.5rem);
          align-items: start;
        }
        .shop-sidebar-desktop { display: block; }
        .shop-filter-toggle   { display: none; }

        @media (max-width: 768px) {
          .shop-layout { grid-template-columns: 1fr; }
          .shop-sidebar-desktop { display: none; }
          .shop-filter-toggle   { display: flex; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", fontFamily: T.ffBody, background: T.ivoryDark }}>

        {/* Hero header */}
        <div style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(192,80,110,.1) 0%, transparent 70%), ${T.ivory}`,
          textAlign: "center",
          padding: "clamp(2.5rem, 7vw, 5rem) 1rem clamp(2rem, 5vw, 4rem)",
          borderBottom: `1px solid ${T.warm100}`,
          position: "relative",
        }}>
          <AccentBar />
          <p style={{ margin: "0 0 .4rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: T.rose }}>
            GlossyLuxHair Collection
          </p>
          <h1 style={{ margin: "0 0 .6rem", fontFamily: T.ffDisplay, fontSize: "clamp(2.2rem, 6vw, 3.5rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
            Our Shop
          </h1>
          <p style={{ margin: 0, fontSize: "clamp(13px, 2vw, 15px)", color: T.warm400, maxWidth: "42ch", marginInline: "auto", lineHeight: 1.6 }}>
            Discover luxury hair products crafted for every texture, every occasion.
          </p>
        </div>

        {/* Main layout */}
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "clamp(1.5rem, 4vw, 3rem) clamp(1rem, 4vw, 2rem)" }}>
          <div className="shop-layout">

            {/* Desktop sidebar */}
            <aside className="shop-sidebar-desktop" style={{
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${T.warm100}`,
              overflow: "hidden",
              boxShadow: `0 2px 8px rgba(26,21,16,.04)`,
              position: "sticky",
              top: "5rem",
            }}>
              <AccentBar />
              <div style={{ padding: "1.25rem" }}>
                <SidebarContent {...sidebarProps} />
              </div>
            </aside>

            {/* Product area */}
            <main>

              {/* Top bar */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
                <p style={{ margin: 0, fontSize: 13, color: T.warm400, fontFamily: T.ffBody }}>
                  {loading ? "Loading…" : `${filtered.length} product${filtered.length !== 1 ? "s" : ""}`}
                </p>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {/* Active filter pills */}
                  {activeFilters.map((f) => (
                    <button
                      key={f}
                      onClick={() => toggleFilter(f)}
                      style={{
                        padding: "4px 10px",
                        fontFamily: T.ffBody, fontSize: 12, fontWeight: 600,
                        borderRadius: 99,
                        border: `1.5px solid ${T.rose}`,
                        background: T.rose, color: "#fff",
                        cursor: "pointer",
                      }}
                    >✕ {f}</button>
                  ))}

                  {/* Mobile filter button */}
                  <button
                    className="shop-filter-toggle"
                    onClick={() => setDrawerOpen(true)}
                    style={{
                      padding: "7px 14px",
                      fontFamily: T.ffBody, fontSize: 13, fontWeight: 600,
                      color: T.warm700,
                      background: "#fff",
                      border: `1.5px solid ${T.warm200}`,
                      borderRadius: 99, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    ⚙ Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
                  </button>
                </div>
              </div>

              {/* Product grid */}
              {loading ? (
                <Spinner />
              ) : paginated.length === 0 ? (
                <div style={{ textAlign: "center", padding: "4rem 0" }}>
                  <div style={{ fontSize: 40, marginBottom: "1rem" }}>🔍</div>
                  <p style={{ fontFamily: T.ffDisplay, fontSize: "1.2rem", color: T.warm700, margin: "0 0 .5rem" }}>
                    No products found
                  </p>
                  <p style={{ fontSize: 13, color: T.warm400 }}>Try adjusting your filters.</p>
                  <button
                    onClick={clearFilters}
                    style={{
                      marginTop: "1rem",
                      padding: "0.65rem 1.5rem",
                      fontFamily: T.ffBody, fontWeight: 600, fontSize: 13,
                      color: "#fff", background: T.rose,
                      border: "none", borderRadius: 10, cursor: "pointer",
                    }}
                  >Clear Filters</button>
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 210px), 1fr))",
                  gap: "clamp(1rem, 2.5vw, 1.5rem)",
                }}>
                  {paginated.map((product, idx) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      idx={idx}
                      wished={wished.has(product.id)}
                      onWish={toggleWish}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>
              )}

              <Pager
                page={page}
                total={filtered.length}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              />
            </main>

          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <SidebarContent {...sidebarProps} />
      </FilterDrawer>
    </>
  );
};

export default ShopPage;